import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import axiosInstance from "../api/axiosInstance";

const MonthlyIncomeGraph = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [monthlyData, setMonthlyData] = useState({ categories: [], totals: [] });
  const [dailyData, setDailyData]     = useState({ categories: [], totals: [] });
  const [dailyError, setDailyError]   = useState(null);
  const [viewMode, setViewMode]       = useState("monthly");

  const getFontSize = () => {
    if (windowWidth > 3000) return 24;
    if (windowWidth > 2500) return 19;
    if (windowWidth >= 2000) return 15;
    if (windowWidth >= 1600) return 13;
    return 11;
  };

  const getChartHeight = () => {
    if (windowWidth > 3000) return 500;
    if (windowWidth > 2500) return 380;
    if (windowWidth >= 2000) return 320;
    if (windowWidth >= 1600) return 270;
    return 250;
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Fetch monthly income ────────────────────────────────────────────────────
  const fetchMonthlyData = async () => {
    try {
      const response = await axiosInstance.get("dashboard/payments_graph");
      if (response.status === 200 && response.data.success) {
        const apiData = response.data.data || [];
        const months = [
          "January","February","March","April","May","June",
          "July","August","September","October","November","December",
        ];
        const shortMonths = [
          "Jan","Feb","Mar","Apr","May","Jun",
          "Jul","Aug","Sep","Oct","Nov","Dec",
        ];
        const apiMap = {};
        apiData.forEach((item) => { apiMap[item.month] = item.total; });
        const totals = months.map((m) => apiMap[m] || 0);
        setMonthlyData({ categories: shortMonths, totals });
      }
    } catch (error) {
      console.error("Monthly income API Error:", error);
    }
  };

  // ── Fetch daily income ──────────────────────────────────────────────────────
  // Expects: { success: true, data: [{ date: "2024-01-01", total: 250.00 }, ...] }
  const fetchDailyData = async () => {
    try {
      const response = await axiosInstance.get("dashboard/payments_graph_daily");
      if (response.status === 200 && response.data.success) {
        const apiData = response.data.data || [];
        if (apiData.length === 0) {
          setDailyError("No daily payment data found.");
        } else {
          setDailyData({
            categories: apiData.map((item) => item.date),
            totals:     apiData.map((item) => item.total),
          });
        }
      } else {
        setDailyError("Failed to load daily data.");
      }
    } catch (error) {
      console.error("Daily income API Error:", error);
      const detail = error.response?.data?.error || error.response?.data?.message || error.message;
      setDailyError("Error fetching daily data: " + detail);
    }
  };

  useEffect(() => {
    Promise.all([fetchMonthlyData(), fetchDailyData()]).finally(() =>
      setLoading(false)
    );
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────
  const isDaily    = viewMode === "daily";
  const categories = isDaily ? dailyData.categories : monthlyData.categories;
  const seriesData = isDaily ? dailyData.totals      : monthlyData.totals;

  const chartOptions = {
    chart: {
      height: getChartHeight(),
      type: "area",
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      events: {},
    },

    dataLabels: { enabled: false },

    stroke: {
      curve: "smooth",
      width: 1.5,
    },

    // ── Green palette ─────────────────────────────────────────────────────────
    colors: ["#2ca87f"],

    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.5,
        opacityTo: 0,
        colorStops: [
          { offset: 0,   color: "#2ca87f40", opacity: 0.6 },
          { offset: 100, color: "#FFFFFF",   opacity: 0.1 },
        ],
      },
    },

    grid: {
      show: true,
      strokeDashArray: 3,
      borderColor: "#eeeeee",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true  } },
    },

    xaxis: {
      type: "category",
      categories,
      tickAmount: isDaily ? Math.min(30, categories.length) : undefined,
      labels: {
        rotate: isDaily ? -45 : 0,
        rotateAlways: isDaily,
        style: {
          colors: "rgb(91,107,121)",
          fontSize: getFontSize() + "px",
        },
        formatter: isDaily
          ? (val) => {
              if (!val) return val;
              const d = new Date(val);
              return isNaN(d)
                ? val
                : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
            }
          : undefined,
      },
    },

    yaxis: {
      min: 0,
      tickAmount: 6,
      labels: {
        style: {
          fontSize: getFontSize() + "px",
          colors: "rgb(91,107,121)",
        },
        // Show BHD currency on Y axis
        formatter: (val) => `${parseFloat(val).toFixed(0)} BHD`,
      },
    },

    tooltip: {
      x: {
        formatter: isDaily
          ? (val, { dataPointIndex }) => {
              const raw = dailyData.categories[dataPointIndex];
              if (!raw) return val;
              const d = new Date(raw);
              return isNaN(d)
                ? raw
                : d.toLocaleDateString("en-GB", {
                    weekday: "short", day: "2-digit",
                    month: "short",   year: "numeric",
                  });
            }
          : (val) => val,
      },
      y: {
        formatter: (val) => `${parseFloat(val).toFixed(3)} BHD`,
      },
    },

    title: { text: "" },
  };

  const chartSeries = [{ name: "Income (BHD)", data: seriesData }];

  if (loading) return <p>Loading chart...</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8, paddingLeft: 12 }}>
        <button
          onClick={() => setViewMode("monthly")}
          style={{
            padding: "3px 12px",
            borderRadius: 4,
            border: "1px solid #2ca87f",
            background: viewMode === "monthly" ? "#2ca87f" : "transparent",
            color: viewMode === "monthly" ? "#fff" : "#2ca87f",
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          Monthly
        </button>
        <button
          onClick={() => setViewMode("daily")}
          style={{
            padding: "3px 12px",
            borderRadius: 4,
            border: "1px solid #2ca87f",
            background: viewMode === "daily" ? "#2ca87f" : "transparent",
            color: viewMode === "daily" ? "#fff" : "#2ca87f",
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          Daily
        </button>
      </div>
      <div id="income-chart">
        {isDaily && dailyError ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: getChartHeight(), color: "#888", fontSize: 13 }}>
            {dailyError}
          </div>
        ) : (
          <ReactApexChart
            key={`income-${viewMode}`}
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={getChartHeight()}
          />
        )}
      </div>
    </div>
  );
};

export default MonthlyIncomeGraph;
